import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FaLock } from 'react-icons/fa';
import {
  DEFAULT_DISPLAY_SETTINGS,
  andDisplaySettings,
  normalizeDisplaySettings
} from '../utils/displaySettings';
import './UserManagement.css';
import './DisplaySettings.css';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const COLUMNS = [
  { key: 'showFat', label: 'Fat' },
  { key: 'showScc', label: 'SCC' },
  { key: 'showAlcohol68', label: 'Alc 68%' },
  { key: 'showAlcohol70', label: 'Alc 70%' },
  { key: 'showAlcohol72', label: 'Alc 72%' }
];

const ALL_ON = { ...DEFAULT_DISPLAY_SETTINGS };

const idsMatch = (a, b) => String(a) === String(b);

const withEffectiveTree = (clients = []) =>
  clients.map((client) => {
    const clientOwn = normalizeDisplaySettings(client.displaySettings);
    return {
      ...client,
      displaySettings: clientOwn,
      effectiveDisplaySettings: clientOwn,
      managers: (client.managers || []).map((manager) => {
        const managerOwn = normalizeDisplaySettings(manager.displaySettings);
        const managerEffective = andDisplaySettings(clientOwn, managerOwn);
        return {
          ...manager,
          displaySettings: managerOwn,
          effectiveDisplaySettings: managerEffective,
          employees: (manager.employees || []).map((employee) => {
            const employeeOwn = normalizeDisplaySettings(employee.displaySettings);
            return {
              ...employee,
              displaySettings: employeeOwn,
              effectiveDisplaySettings: andDisplaySettings(managerEffective, employeeOwn)
            };
          })
        };
      }),
      employees: (client.employees || []).map((employee) => {
        const employeeOwn = normalizeDisplaySettings(employee.displaySettings);
        return {
          ...employee,
          displaySettings: employeeOwn,
          effectiveDisplaySettings: andDisplaySettings(clientOwn, employeeOwn)
        };
      })
    };
  });

const updateUserInTree = (clients, userId, patch) =>
  clients.map((client) => {
    if (idsMatch(client.id, userId)) {
      return { ...client, ...patch };
    }
    return {
      ...client,
      managers: (client.managers || []).map((manager) => {
        if (idsMatch(manager.id, userId)) {
          return { ...manager, ...patch };
        }
        return {
          ...manager,
          employees: (manager.employees || []).map((employee) =>
            idsMatch(employee.id, userId) ? { ...employee, ...patch } : employee
          )
        };
      }),
      employees: (client.employees || []).map((employee) =>
        idsMatch(employee.id, userId) ? { ...employee, ...patch } : employee
      )
    };
  });

const DisplaySettings = () => {
  const [clients, setClients] = useState([]);
  const [expandedClients, setExpandedClients] = useState({});
  const [expandedManagers, setExpandedManagers] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState('');
  const [error, setError] = useState('');

  const fetchTree = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BASE_URL}/api/display-settings`);
      const tree = withEffectiveTree(response.data || []);
      setClients(tree);

      const nextClients = {};
      const nextManagers = {};
      tree.forEach((client) => {
        nextClients[client.id] = true;
        (client.managers || []).forEach((manager) => {
          nextManagers[manager.id] = true;
        });
      });
      setExpandedClients(nextClients);
      setExpandedManagers(nextManagers);
      setError('');
    } catch (err) {
      console.error('Error fetching display settings:', err);
      setError(err.response?.data?.message || 'Failed to load display settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTree();
  }, []);

  const handleToggle = async (user, key, parentEffective) => {
    const parentAllows = (parentEffective?.[key] ?? 1) === 1;
    if (!parentAllows) return;

    const current = user.displaySettings?.[key] ?? 1;
    const nextValue = current === 1 ? 0 : 1;
    const requestKey = `${user.id}-${key}`;
    setSavingKey(requestKey);
    setError('');

    const previous = clients;
    setClients((list) =>
      withEffectiveTree(
        updateUserInTree(list, user.id, {
          displaySettings: {
            ...DEFAULT_DISPLAY_SETTINGS,
            ...user.displaySettings,
            [key]: nextValue
          }
        })
      )
    );

    try {
      const response = await axios.put(`${BASE_URL}/api/display-settings/${user.id}`, {
        [key]: nextValue
      });
      setClients((list) =>
        withEffectiveTree(
          updateUserInTree(list, user.id, {
            displaySettings: response.data.displaySettings,
            effectiveDisplaySettings: response.data.effectiveDisplaySettings
          })
        )
      );
    } catch (err) {
      console.error('Error updating display settings:', err);
      setClients(previous);
      setError(err.response?.data?.message || 'Failed to update display settings');
    } finally {
      setSavingKey('');
    }
  };

  const renderFlags = (user, parentEffective, lockedBy) => {
    const own = { ...DEFAULT_DISPLAY_SETTINGS, ...user.displaySettings };
    return (
      <div className="ds-flags">
        {COLUMNS.map((col) => {
          const parentAllows = (parentEffective?.[col.key] ?? 1) === 1;
          const ownOn = own[col.key] === 1;
          const visible = parentAllows && ownOn;
          const locked = !parentAllows;
          const busy = savingKey === `${user.id}-${col.key}`;
          const title = locked
            ? `Locked Off by ${lockedBy}. Turn it On there to enable this row.`
            : `${col.label} is ${visible ? 'On' : 'Off'} for ${user.name}`;

          return (
            <div key={col.key} className="ds-flag">
              <span className="ds-flag-label">{col.label}</span>
              <button
                type="button"
                className={`ds-switch ${visible ? 'is-on' : 'is-off'} ${locked ? 'is-locked' : ''}`}
                onClick={() => handleToggle(user, col.key, parentEffective)}
                disabled={busy || locked}
                aria-pressed={visible}
                title={title}
              >
                <span className="ds-switch-knob">{locked ? <FaLock /> : null}</span>
                <span className="ds-switch-text">{locked ? 'Locked' : visible ? 'On' : 'Off'}</span>
              </button>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="user-management">
        <div className="loading-state">Loading display settings...</div>
      </div>
    );
  }

  return (
    <div className="user-management display-settings">
      <div className="header-section">
        <div className="header-content">
          <h1>Display Settings</h1>
        </div>
      </div>
      <p className="ds-help">
        Hierarchy is Client → Manager → Employee. Turning a field Off at a higher level freezes it
        Off for everyone below. Lower levels cannot turn it back on until the parent is On again.
      </p>

      {error && <div className="error-message">{error}</div>}

      {clients.length === 0 ? (
        <div className="empty-state">
          <p>No clients found</p>
        </div>
      ) : (
        <div className="users-list">
          {clients.map((client) => {
            const clientOpen = expandedClients[client.id] !== false;
            const managerCount = (client.managers || []).length;
            const employeeCount =
              (client.employees || []).length +
              (client.managers || []).reduce((sum, manager) => sum + (manager.employees || []).length, 0);

            return (
              <div key={client.id} className="user-card ds-client-card">
                <div className="user-card-header ds-node-header">
                  <div className="user-info-inline">
                    <h3>{client.name}</h3>
                    <span className="user-username">@{client.username}</span>
                    <span className="ds-level-tag client">Client</span>
                  </div>
                  <button
                    type="button"
                    className="btn-view-details"
                    onClick={() =>
                      setExpandedClients((prev) => ({ ...prev, [client.id]: !clientOpen }))
                    }
                  >
                    {clientOpen ? 'Hide team' : 'View team'}
                  </button>
                </div>
                {renderFlags(client, ALL_ON, '')}
                <div className="ds-meta">{managerCount} managers · {employeeCount} employees</div>

                {clientOpen && (
                  <div className="hierarchy-section">
                    <h4>Managers</h4>
                    {(client.managers || []).length === 0 && (client.employees || []).length === 0 ? (
                      <p className="empty-message">No managers or employees under this client</p>
                    ) : null}

                    {(client.managers || []).map((manager) => {
                      const managerOpen = expandedManagers[manager.id] !== false;
                      const teamSize = (manager.employees || []).length;
                      return (
                        <div key={manager.id} className="manager-detail-card">
                          <div className="sub-user-card ds-node">
                            <div className="ds-node-header">
                              <div className="user-info-inline">
                                <span>{manager.name}</span>
                                <span className="user-username">@{manager.username}</span>
                                <span className="ds-level-tag manager">Manager</span>
                              </div>
                              {teamSize > 0 && (
                                <button
                                  type="button"
                                  className="btn-view-details"
                                  onClick={() =>
                                    setExpandedManagers((prev) => ({
                                      ...prev,
                                      [manager.id]: !managerOpen
                                    }))
                                  }
                                >
                                  {managerOpen ? 'Hide employees' : `View employees (${teamSize})`}
                                </button>
                              )}
                            </div>
                            {renderFlags(manager, client.effectiveDisplaySettings, `Client ${client.name}`)}
                          </div>

                          {managerOpen && teamSize > 0 && (
                            <div className="employees-hierarchy-section">
                              <h5>Employees under {manager.name}</h5>
                              <div className="employees-list-nested">
                                {(manager.employees || []).map((employee) => (
                                  <div key={employee.id} className="employee-card-nested ds-node">
                                    <div className="ds-node-header">
                                      <div className="user-info-inline">
                                        <strong>{employee.name}</strong>
                                        <span className="user-username">@{employee.username}</span>
                                        <span className="ds-level-tag employee">Employee</span>
                                      </div>
                                    </div>
                                    {renderFlags(
                                      employee,
                                      manager.effectiveDisplaySettings,
                                      `Manager ${manager.name}`
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {(client.employees || []).length > 0 && (
                      <div className="employees-hierarchy-section">
                        <h5>Employees reporting to client</h5>
                        <div className="employees-list-nested">
                          {(client.employees || []).map((employee) => (
                            <div key={employee.id} className="employee-card-nested ds-node">
                              <div className="ds-node-header">
                                <div className="user-info-inline">
                                  <strong>{employee.name}</strong>
                                  <span className="user-username">@{employee.username}</span>
                                  <span className="ds-level-tag employee">Employee</span>
                                </div>
                              </div>
                              {renderFlags(
                                employee,
                                client.effectiveDisplaySettings,
                                `Client ${client.name}`
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DisplaySettings;
